// src/LeadView.jsx
import React from "react";
import { useParams } from "react-router-dom";
import { supabase } from "./lib/supabaseClient";

export default function LeadView() {
  const { slug } = useParams();
  const [lead, setLead] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;

    async function loadLead() {
      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from("leads")
          .select("id, user_name, note, design_images, placement_image")
          .eq("public_slug", slug)
          .single();

        if (error) {
          console.error("[lead-view] select error", error);
          throw error;
        }

        if (cancelled) return;
        setLead(data);
      } catch (err) {
        console.error("[lead-view] unexpected error", err);
        if (!cancelled) {
          setError("This preview link is invalid or has expired.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (slug) loadLead();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#111",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
        }}
      >
        Loading tattoo preview…
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#111",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 8,
          padding: 24,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 24 }}>Preview not available</h1>
        <p style={{ opacity: 0.7, maxWidth: 420, textAlign: "center" }}>
          {error || "We couldn’t find this preview link. It may have expired or been removed."}
        </p>
      </div>
    );
  }

  const designs = lead.design_images || [];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#111",
        color: "#fff",
        padding: "32px 16px 40px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div style={{ width: "100%", maxWidth: 960 }}>
        <header style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, opacity: 0.6, marginBottom: 4 }}>
            Tattune preview
          </div>
          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700 }}>
            Tattoo preview
          </h1>
          <p style={{ opacity: 0.7, marginTop: 8, marginBottom: 0 }}>
            For <strong>{lead.user_name || "a Tattune guest"}</strong>
          </p>
        </header>

        {lead.note && (
          <section style={{ marginBottom: 24 }}>
            <h3 style={{ margin: 0, marginBottom: 4, fontSize: 16 }}>Notes</h3>
            <p style={{ margin: 0, opacity: 0.9 }}>{lead.note}</p>
          </section>
        )}

        {designs.length > 0 && (
          <section style={{ marginBottom: 24 }}>
            <h3 style={{ margin: 0, marginBottom: 8, fontSize: 16 }}>
              Design preview
            </h3>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
              }}
            >
              {designs.map((url) => (
                <img
                  key={url}
                  src={url}
                  alt="Tattoo design"
                  style={{
                    maxWidth: 420,
                    width: "100%",
                    borderRadius: 18,
                    background: "#222",
                  }}
                />
              ))}
            </div>
          </section>
        )}

        {lead.placement_image && (
          <section style={{ marginBottom: 24 }}>
            <h3 style={{ margin: 0, marginBottom: 8, fontSize: 16 }}>
              Placement photo
            </h3>
            <img
              src={lead.placement_image}
              alt="Placement"
              style={{
                maxWidth: 420,
                width: "100%",
                borderRadius: 18,
                background: "#222",
              }}
            />
          </section>
        )}

        <footer style={{ fontSize: 11, opacity: 0.5, marginTop: 24 }}>
          Sent via <strong>Tattune</strong> – Connecting imagination with pigmentation.
        </footer>
      </div>
    </div>
  );
}